import { useCallback, useMemo, useState } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import {
  ATTENDANCE_STATUS,
  ATTENDANCE_TILE,
  PARTICIPANT_NOTE_STATE,
} from "../group-visit-constant";
import {
  AttendanceStatus,
  AttendanceTally,
  SessionParticipant,
} from "../group-visit-types";
import { isNoteRequired } from "../group-visit-utils";

export type UseSessionAttendance = {
  participants: SessionParticipant[];
  tally: AttendanceTally[];
  searchKey: string;
  setSearchKey: (value: string) => void;
  setStatus: (uuid: string, status: AttendanceStatus) => void;
  markAllPresent: () => void;
  /** Clears every participant's attendance. */
  unmarkAll: () => void;
  isAllPresent: boolean;
  /** Everyone who owes an individual note, in roster order. */
  attendees: SessionParticipant[];
  attendeeCount: number;
  completedNotes: number;
  requiredNotes: number;
};

type StatusOverrides = Record<string, AttendanceStatus | null>;

/**
 * Attendance for one session — presence per participant, the tally the rail
 * shows, and how many individual notes are still owed. Presence is kept on top
 * of the roster so a refetched roster keeps the marks already made.
 *
 * @author Rajkumar Rathod
 */
export const useSessionAttendance = (
  roster: SessionParticipant[]
): UseSessionAttendance => {
  const [overrides, setOverrides] = useState<StatusOverrides>({});
  const [debouncedSearch, setSearchKey, searchKey] = useDebounce("");

  const participants = useMemo(
    () =>
      roster.map((participant) => ({
        ...participant,
        // An override of null is a deliberate unmark, not a missing one.
        status:
          participant.uuid in overrides
            ? overrides[participant.uuid]
            : participant.status,
      })),
    [roster, overrides]
  );

  const setStatus = useCallback((uuid: string, status: AttendanceStatus) => {
    setOverrides((current) => ({ ...current, [uuid]: status }));
  }, []);

  const markAllPresent = useCallback(() => {
    setOverrides(
      Object.fromEntries(
        roster.map((participant) => [
          participant.uuid,
          ATTENDANCE_STATUS.PRESENT,
        ])
      )
    );
  }, [roster]);

  const unmarkAll = useCallback(() => {
    setOverrides(
      Object.fromEntries(roster.map((participant) => [participant.uuid, null]))
    );
  }, [roster]);

  const isAllPresent =
    participants.length > 0 &&
    participants.every(
      (participant) => participant.status === ATTENDANCE_STATUS.PRESENT
    );

  const tally = useMemo<AttendanceTally[]>(
    () =>
      Object.values(ATTENDANCE_TILE).map((id) => ({
        id,
        count:
          id === ATTENDANCE_TILE.TOTAL
            ? participants.length
            : participants.filter((participant) => participant.status === id)
                .length,
      })),
    [participants]
  );

  const visibleParticipants = useMemo(() => {
    const search = debouncedSearch.trim().toLowerCase();
    if (!search) return participants;

    return participants.filter(
      (participant) =>
        participant.name.toLowerCase().includes(search) ||
        (participant.patientId ?? "").toLowerCase().includes(search)
    );
  }, [participants, debouncedSearch]);

  const owingNotes = useMemo(
    () =>
      participants.filter((participant) => isNoteRequired(participant.status)),
    [participants]
  );

  return {
    participants: visibleParticipants,
    tally,
    searchKey,
    setSearchKey,
    setStatus,
    markAllPresent,
    unmarkAll,
    isAllPresent,
    attendees: owingNotes,
    attendeeCount: owingNotes.length,
    completedNotes: owingNotes.filter(
      (participant) => participant.noteState === PARTICIPANT_NOTE_STATE.COMPLETE
    ).length,
    // Everyone but a no-show owes a note, including attendees not yet marked.
    requiredNotes: participants.filter(
      (participant) => participant.status !== ATTENDANCE_STATUS.NO_SHOW
    ).length,
  };
};
